import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ImageToSave } from '../../images/models/image-to-save.model';
import { VehicleListValue } from '../../walk-in/models/vehicle-list-value.model';
import { Signature } from '../../invoice/models/signature.model';
import { SignatureInputComponent } from '../../signature-input/signature-input.component';
import { catchError, EMPTY, Subject, take, takeUntil } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ImageService } from '../../services/image.service';
import { PathFinderService } from '../../services/path-finder.service';
import { NotificationService } from '../../services/notification.service';
import { StockService } from '../../services/stock.service';
import { AssetTransfer } from './models/asset-transfer.model';
import { ImageInputComponent } from '../../images/image-input/image-input.component';
import { StockInventory } from '../models/stock-inventory.model';
import { StockInventoryComponent } from '../stock-inventory/stock-inventory.component';
import { AssetsTransferEnum } from './models/viewEnum';

@Component({
  selector: 'app-asset-transfer',
  templateUrl: './asset-transfer.component.html',
  styleUrl: './asset-transfer.component.scss',
  encapsulation: ViewEncapsulation.None,
})

export class AssetTransferComponent implements OnInit, AfterViewInit, OnDestroy {
  constructor(private modalService: NgbModal,
    private formBuilder: UntypedFormBuilder,
    private imageService: ImageService,
    private pathFinder: PathFinderService,
    private notifications: NotificationService,
    private stockService: StockService) {
  }

  @ViewChild(ImageInputComponent) imageInputComponent: ImageInputComponent;
  stockInventoryComponent: StockInventoryComponent;
  saveNotifier: Subject<number> = new Subject();
  backNotifier: Subject<number> = new Subject();
  destroy$ = new Subject();
  assetTransferForm: UntypedFormGroup;
  currentView = 1;
  viewPage = AssetsTransferEnum;
  currentPage = 1;
  imgs: Array<ImageToSave> = [];
  showMessage: boolean = true;
  pageCount: number;
  driverSignature: Signature;
  pageSize: number = 1;
  vehicle: VehicleListValue;
  driverCheckURL: string = environment.assetTransferDriverCheckUrl;
  isImageAdded: boolean;
  driverImage: ImageToSave;
  siteId: number;
  siteName: string;
  canContinue: boolean;

  ngOnInit(): void {
    this.assetTransferForm = this.formBuilder.group({
      collectionReference: ['', [Validators.required, Validators.maxLength(70)]],
      transporterVRM: ['', [Validators.maxLength(8)]],
      transportCompanyName: ['', [Validators.maxLength(70)]],
      driverName: ['', [Validators.required, Validators.maxLength(64)]]
    });

    this.imgs.push(this.createDriverIdImage());
    this.pageTransfer();

    this.pathFinder.getPurchaseSettings$().pipe(
      take(1),
      catchError(err => {
        this.notifications.dangerToast('Unable to retrieve purchase settings.', err);
        return EMPTY;
      }),
    ).subscribe();
  }

  ngAfterViewInit(): void {
    this.imageService.imageAdded
      .pipe(takeUntil(this.destroy$)).subscribe((value) => {
        this.isImageAdded = value;
        if (this.imageInputComponent) {
          this.driverImage = this.imageInputComponent?.imagesToSave?.[0];
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }


  dismissModal() {
    this.stockService.multipleVehicleDescriptions = [];
    this.modalService.dismissAll();
    this.stockInventoryComponent.stockItems.forEach(item => { item.leftBranch = false });
  }

  handleNext(): void {
    const isLastItemOnPage =
      (this.currentPage === this.pageCount);

    if (isLastItemOnPage) {
      this.nextView(AssetsTransferEnum.DriverSignature, true);
    } else {
      this.nextItemPage();
    }
  }

  nextView(view: AssetsTransferEnum, showMessage: boolean) {
    this.showMessage = showMessage;
    this.currentView = view;
    if (view === AssetsTransferEnum.Assets) {
      this.checkVanControl();
    }
    else if (view === AssetsTransferEnum.DriverSignature) {
      this.modalService.dismissAll();
      this.buyerSign();
    }
  }

  pageTransfer(): void {
    this.pageCount = Math.ceil(
      this.stockService.multipleVehicleDescriptions?.length / this.pageSize
    );

    this.currentPage = Math.min(
      Math.max(this.currentPage, 1),
      this.pageCount || 1
    );

    this.vehicle =
      this.stockService.multipleVehicleDescriptions?.[this.currentPage - 1] ?? null;

    this.checkVanControl();
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage -= 1;
      this.vehicle =
        this.stockService.multipleVehicleDescriptions?.[this.currentPage - 1] ?? null;
    }
  }

  nextItemPage() {
    this.checkVanControl();
    if (this.currentPage < this.pageCount) {
      this.currentPage += 1;
      this.vehicle =
        this.stockService.multipleVehicleDescriptions?.[this.currentPage - 1] ?? null;
    }
  }

  buyerSign() {
    const modalRef = this.modalService.open(SignatureInputComponent, {
      keyboard: false,
      backdrop: 'static',
      centered: true,
      size: 'xl',
      windowClass: 'signatureWindow'
    });
    modalRef.componentInstance.isWbacSignature = false;
    modalRef.componentInstance.isDriverSignature = true;
    modalRef.componentInstance.quoteStateId = 1;
    modalRef.componentInstance.htmlInput = '<span class="blue-text">Signed by Branch Manager:</span> By signing below, you <span class="green-text">confirm that all information</span> provided is <span class="green-text">accurate and that responsibility</span> for the listed assets has been <span class="green-text">formally transferred.</span>';
    modalRef.result.then(result => {

      if (result) {
        this.driverSignature = result;
        const inventory = new StockInventory();
        inventory.siteId = this.siteId;
        inventory.siteName = this.siteName;
        inventory.items = this.stockInventoryComponent.stockItems.filter((v) => v.leftBranch === true || v.markAsNonRunner === true);
        this.stockService.saveStockInventory$(inventory).subscribe(() => {
          this.stockInventoryComponent.loadData();
          this.saveAssetTransfer();
        });
      } else {
        this.stockService.multipleVehicleDescriptions = [];
      }
    }, () => {
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mapVehicleItems(vehicle: any) {
    return Object.entries(vehicle.Form.value)
      .filter(([key, value]) => key !== 'vanEmpty' || !!value)
      .map(([key, value]) => ({
        quoteStateId: vehicle.QuoteStateId,
        vehicleItemName: this.formatVehicleItemName(key),
        isPresent: !!value
      }));
  }

  private formatVehicleItemName(key: string): string {
    return key
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/^./, (firstLetter) => firstLetter.toUpperCase());
  }

  saveAssetTransfer(): void {
    const formValue = this.assetTransferForm.getRawValue();
    const assetTransfer: AssetTransfer = {
      ...formValue,
      signatureImage: this.driverSignature,
      vehicleItems: this.stockService.multipleVehicleDescriptions.flatMap(vehicle =>
        this.mapVehicleItems(vehicle)
      )
    };

    this.stockService
      .saveDailyStockAssetTransfer$(assetTransfer, this.driverImage)
      .subscribe(() => {
        this.stockService.multipleVehicleDescriptions.length = 0;
        this.stockInventoryComponent.stockItems.forEach(item => { item.leftBranch = false })
      });
  }

  checkVanControl(): void {
    const vans = this.stockService.multipleVehicleDescriptions?.filter(v => v.IsVan);
    vans?.forEach(van => {
      const control = van.Form.get('vanEmpty');

      if (!control) {
        return;
      }

      control.enable();
      const updateState = () => {

        if (this.canContinue) {
          control.clearValidators();
        } else {
          control.setValidators([Validators.required]);
        }

        control.updateValueAndValidity({ emitEvent: false });
      };

      updateState();
      control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => updateState());
    });
  }

  canContinueForVehicle(vehicle: VehicleListValue): boolean {
    const control = vehicle.Form.get('vanEmpty');
    if (!vehicle.IsVan) {
      return true;
    }
    return control?.value === 1;
  }

  createDriverIdImage(): ImageToSave {
    return {
      documentName: 'Driver I.D.',
      stampCode: 'ID',
      parameterName: 'DriverID'
    } as ImageToSave;
  }

  get isBackButtonActive(): boolean {
    return this.pageCount > 0;
  }

  get isFirstPage(): boolean {
    return this.currentPage === 1;
  }
}
